import React, { useState, useEffect, useMemo } from "react";
import { apiGet } from "../../services/api";
import type { Diagnosis } from "../../types/domain";
import "./DiagnosisConsole.css";

interface DiagnosisConsoleProps {
    visitId: string | number;
    isLocked?: boolean;
    selectedDiagnoses?: Diagnosis[];
    onAdd?: (diagnosis: Diagnosis) => void;
    onRemove?: (id: number) => void;
}

export default function DiagnosisConsole({ visitId, isLocked = false, selectedDiagnoses = [], onAdd, onRemove }: DiagnosisConsoleProps) {
    const [searchTerm, setSearchTerm] = useState("");
    const [allDiagnoses, setAllDiagnoses] = useState<Diagnosis[]>([]);

    // Local state if controlled prop not provided (though parent should really control it now)
    const [localSelected, setLocalSelected] = useState<Diagnosis[]>(selectedDiagnoses);

    useEffect(() => {
        setLocalSelected(selectedDiagnoses);
    }, [selectedDiagnoses]);

    useEffect(() => {
        const loadDiagnoses = async () => {
            try {
                const data = await apiGet("/api/admin/diagnosis");
                if (Array.isArray(data)) {
                    setAllDiagnoses((data as Diagnosis[]).filter(d => d.is_active !== false));
                }
            } catch (e) {
                console.error("Failed to load diagnosis list", e);
            }
        };
        loadDiagnoses();
    }, []);

    const filteredDiagnoses = useMemo(() => {
        if (!searchTerm.trim()) return allDiagnoses; // Show all by default
        const lower = searchTerm.toLowerCase();
        return allDiagnoses.filter(d =>
            d.description.toLowerCase().includes(lower) ||
            d.code.toLowerCase().includes(lower)
        );
    }, [allDiagnoses, searchTerm]);

    const handleAdd = (d: Diagnosis) => {
        if (isLocked) return;
        if (localSelected.some(sel => sel.id === d.id)) return;

        const newSelected = [...localSelected, d];
        setLocalSelected(newSelected);
        onAdd?.(d);
    };

    const handleRemove = (id: number) => {
        if (isLocked) return;
        const newSelected = localSelected.filter(d => d.id !== id);
        setLocalSelected(newSelected);
        onRemove?.(id);
    };

    return (
        <div className="diagnosis-console-card">
            <div className="diagnosis-console-header">
                <h3 className="diagnosis-console-title">🔍 Diagnosis</h3>
            </div>

            <div className="diagnosis-search-row">
                <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search diagnostic codes or names..."
                    disabled={isLocked}
                    className="diagnosis-search-input"
                />
            </div>

            {/* Compact selected chips */}
            {localSelected.length > 0 && (
                <div className="selected-chips-area">
                    <div className="chips-label">Selected:</div>
                    <div className="chips-container">
                        {localSelected.map(d => (
                            <div key={d.id} className="diagnosis-chip">
                                <span className="chip-code">{d.code}</span>
                                <span className="chip-desc">{d.description}</span>
                                {!isLocked && (
                                    <button
                                        type="button"
                                        className="chip-remove"
                                        onClick={() => handleRemove(d.id)}
                                        title="Remove"
                                    >
                                        ✕
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* All diagnoses list - shown by default */}
            <div className="diagnosis-list">
                <div className="diagnosis-list-header">
                    All Diagnoses ({filteredDiagnoses.length})
                </div>
                <div className="diagnosis-list-items">
                    {filteredDiagnoses.length === 0 ? (
                        <div className="mod-empty-state diagnosis-empty">
                            No diagnoses found matching search.
                        </div>
                    ) : (
                        filteredDiagnoses.map(d => {
                            const isSelected = localSelected.some(sel => sel.id === d.id);
                            return (
                                <div
                                    key={d.id}
                                    onClick={() => !isSelected && handleAdd(d)}
                                    className={`diagnosis-item-row ${isSelected ? "is-selected" : ""}`}
                                >
                                    <div className="diagnosis-item-main">
                                        <span className="diagnosis-item-code">
                                            {d.code}
                                        </span>
                                        <span className="diagnosis-item-desc">{d.description}</span>
                                    </div>
                                    {isSelected && <span className="diagnosis-item-check">✓</span>}
                                </div>
                            );
                        })
                    )}
                </div>
            </div>
        </div>
    );
}
